import Bugsnag from '@bugsnag/js';
import posthog from 'posthog-js';

import { clientCookies, userCookies } from '@/shared/constants/cookies';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { useUserStore } from '@/shared/pinia/modules/user';
import { clearCookie, getCookie, setCookie } from '@/shared/utils/cookie';
import { contextualError } from '@/shared/utils/error';
import { hashUserData } from '@/shared/utils/tracking';

const debug_tracking = !!getCookie('debug_tracking');

const userId = () => {
  const loggedInDistinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);
  const distinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);

  if (loggedInDistinctId) {
    return loggedInDistinctId;
  }

  if (distinctId) {
    return distinctId;
  }
};

const userProperties = () => {
  const userStore = useUserStore();
  const configStore = useConfigStore();

  const consentTrackedProperties = {
    country: configStore.countryCode,
    language: configStore.language
  };

  const alwaysTrackedProperties = {
    isLoggedIn: userStore.isLogged
  };

  if (!window.Shopify.customerPrivacy?.preferencesProcessingAllowed()) {
    return alwaysTrackedProperties;
  }

  return {
    ...alwaysTrackedProperties,
    ...consentTrackedProperties
  };
};

const refreshCookieStorage = () => {
  const userStore = useUserStore();

  if (userStore.customer?.uuid) {
    return setCookie(
      clientCookies.PRODUCT_ANALYTICS_USER_ID,
      hashUserData(userStore.customer.uuid),
      365
    );
  }
  clearCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);

  const clientId = getCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);
  if (!clientId) {
    setCookie(
      clientCookies.PRODUCT_ANALYTICS_CLIENT_ID,
      crypto.randomUUID(),
      365
    );
  }
};

export const posthogLog = (...args: Parameters<typeof console.log>) => {
  if (debug_tracking) {
    console.log('[posthog-theme]', ...args);
  }
};

const aliasUser = () => {
  const loggedInDistinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);
  const distinctId = getCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);

  if (loggedInDistinctId && distinctId) {
    posthog.alias(loggedInDistinctId, distinctId);
    clearCookie(clientCookies.PRODUCT_ANALYTICS_CLIENT_ID);
  }
};

export const setPosthogAPIKey = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  const apiKeyCookie = getCookie(clientCookies.POSTHOG_API_KEY);

  if (apiKey && apiKey === apiKeyCookie) {
    return;
  }

  setCookie(clientCookies.POSTHOG_API_KEY, apiKey, 365);
};

const initializePosthog = () => {
  posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_API_HOST,
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: false,
    persistence_name: 'shopify_theme'
  });
};

const checkPosthogFeatureFlags = () => {
  const variant = posthog.getFeatureFlag('a-a-test');
  posthogLog('a-a-test:', variant);
};

const identifyAndSetupUser = (distinctId: string) => {
  if (posthog.get_distinct_id() !== distinctId) {
    posthog.identify(distinctId, userProperties());
    aliasUser();
  }
  checkPosthogFeatureFlags();
};

const getPosthogConsentGiven = (): boolean | null => {
  let consentGiven = false;
  try {
    const consentSettings = JSON.parse(
      getCookie(userCookies.CONSENT_STATUS) ?? 'null'
    );
    consentGiven = !!(consentSettings && consentSettings['PostHog.com']);
  } catch (error) {
    consentGiven = false;
    const errorWithContext = contextualError(
      'Parsing consent settings for Posthog',
      error
    );

    if (errorWithContext && Bugsnag.isStarted()) {
      Bugsnag.notify(errorWithContext);
    }
  }

  return consentGiven;
};

const handleConsentSettings = () => {
  const consentGiven = getPosthogConsentGiven();

  if (consentGiven && posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  } else if (!consentGiven && posthog.has_opted_in_capturing()) {
    posthog.opt_out_capturing();
  }
};

export const initPostHog = () => {
  if (!window.Hobbii?.flags.enableBetaProductAnalytics) {
    return;
  }

  refreshCookieStorage();

  const distinctId = userId();

  if (!distinctId) {
    return;
  }

  initializePosthog();
  identifyAndSetupUser(distinctId);

  window.posthog = posthog;

  handleConsentSettings();
};
